import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || password.length < 6) { toast.error(t('password_too_short')); return; }
    if (password !== confirm) { toast.error(t('passwords_mismatch')); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8">
        <div className="text-center space-y-4">
          <p className="text-gray-500 text-sm">Invalid or missing reset link.</p>
          <Link to="/login"><Button variant="outline" className="text-sm border-gray-200"><ArrowLeft size={14} className="mr-1.5" /> Back to Login</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8" data-testid="reset-password-page">
      <div className="w-full max-w-sm space-y-6 animate-fade-in">
        <div className="flex justify-center mb-4">
          <img src="/inception-logo.png" alt="Sports Hub" className="h-16 w-16 object-contain" />
        </div>

        {done ? (
          <div className="text-center space-y-4">
            <div className="flex justify-center"><CheckCircle size={40} className="text-green-500" /></div>
            <h2 className="text-xl font-semibold text-gray-900">Password Reset</h2>
            <p className="text-gray-400 text-sm">Your password has been updated. You can now login with your new password.</p>
            <Link to="/login">
              <Button className="w-full h-10 text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 rounded-lg mt-4">
                <ArrowLeft size={14} className="mr-1.5" /> Back to Login
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Set New Password</h2>
              <p className="mt-1.5 text-gray-400 text-sm">Enter your new password below</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm text-gray-600">{t('new_password')}</Label>
                <div className="relative">
                  <Input data-testid="reset-password" id="password" type={showPw ? 'text' : 'password'}
                    placeholder="Min 6 characters" value={password} onChange={e => setPassword(e.target.value)}
                    className="bg-white border-gray-200 focus:border-gray-400 h-10 text-sm pr-10" />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm" className="text-sm text-gray-600">{t('confirm_password')}</Label>
                <Input data-testid="reset-confirm" id="confirm" type="password"
                  placeholder="Confirm password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  className="bg-white border-gray-200 focus:border-gray-400 h-10 text-sm" />
              </div>
              <Button data-testid="reset-submit" type="submit" disabled={loading}
                className="w-full h-10 text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 rounded-lg">
                {loading ? '...' : 'Reset Password'}
              </Button>
            </form>
            <Link to="/login" className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600">
              <ArrowLeft size={14} /> {t('back_to_login')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
